export const formatNumber = (number, locale = 'es-AR') => {
  return Number(number || 0).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const formatCurrency = (number, currency = 'ARS', locale = 'es-AR') => {
  return `$ ${formatNumber(number, locale)}`;
}; 

// Formato manual: punto para miles, coma para decimales
export const formatNumberManual = (number) => {
  const parts = Number(number || 0).toFixed(2).split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return parts.join(',');
};

/**
 * Máscara de importe que se completa por la derecha, como en una caja
 * registradora: cada dígito tecleado empuja a los anteriores hacia la
 * izquierda, así que los centavos se llenan primero.
 *
 *   "1" -> 0.01    "17" -> 0.17    "173" -> 1.73    "17305648" -> 173056.48
 *
 * Ignora todo lo que no sea dígito, así que se le puede pasar el texto ya
 * formateado que quedó en el input ("1.730,56") sin limpiarlo antes.
 *
 * @param {string} text - Lo que quedó en el input después de teclear.
 * @param {number} [maxDigits=15] - Tope de dígitos, para no desbordar el número.
 * @returns {string} Importe listo para parseFloat, o '' si no se tecleó ningún dígito.
 */
export const amountFromKeystrokes = (text, maxDigits = 15) => {
  const digits = String(text ?? '')
    .replace(/\D/g, '')
    .replace(/^0+(?=\d)/, '')
    .slice(0, maxDigits);

  if (!digits) return '';

  const cents = digits.padStart(3, '0');
  return `${cents.slice(0, -2)}.${cents.slice(-2)}`;
};

/** @param {number} number - Valor a formatear. @param {string} [symbol='$'] - Símbolo (ej. '$' o 'USD'). */
export const formatCurrencyManual = (number, symbol = '$') => {
  return `${symbol} ${formatNumberManual(number)}`;
}; 
/**
 * Importe abreviado para tarjetas de métrica en pantallas chicas:
 * 4812400 -> "$4,81 M", 128475 -> "$128,5 k".
 */
export const formatCompactCurrency = (number, symbol = '$') => {
  const value = Number(number || 0);
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (abs >= 1_000_000) {
    return `${sign}${symbol}${(abs / 1_000_000).toFixed(2).replace('.', ',')} M`;
  }
  if (abs >= 10_000) {
    return `${sign}${symbol}${(abs / 1_000).toFixed(1).replace('.', ',')} k`;
  }
  // Por debajo de 10.000 no hay sufijo que avise que el número está abreviado,
  // así que se muestra el importe exacto: un "$1.670" a secas se lee como el
  // valor real y no cuadra contra una orden de $1.669,80.
  return `${sign}${symbol}${formatNumberManual(abs)}`;
};

/**
 * Importe compacto para listados densos: sin espacio después del símbolo, pero
 * CON centavos.
 *
 * Antes redondeaba al peso y eso descuadraba contra producción y contra la
 * factura: una orden de $1.669,80 aparecía como $1.670 y una de $1,21 como $1.
 * El ancho que se ganaba no compensa mostrar un importe que no es el real.
 */
export const formatAmountShort = (number, symbol = '$') => {
  const value = Number(number || 0);
  // El signo va antes del símbolo: "-$6,05", no "$-6,05".
  const sign = value < 0 ? '-' : '';
  return `${sign}${symbol}${formatNumberManual(Math.abs(value))}`;
};
